import { useSyncExternalStore } from 'react';
import type { AppState } from '@/core/state';
import { migrate, trimForStorage, type Store } from '@/core/store';
import { setApiKey } from '@/ai/anthropic';
import { clearChat } from '@/ai/chatHistory';
import { api, ApiError } from './api';
import { decodeProgress, encodeProgress } from './codec';
import { passwordKey, passwordProblem } from './passwordKey';

/**
 * Optional account + progress sync.
 *
 *  - Without an account Studio works exactly as before (progress in this browser).
 *  - Signed in, every change is saved to the account a few seconds later.
 *  - Progress is never overwritten silently: if this device and the account
 *    disagree (guest progress at login, or edits on two devices), the learner
 *    chooses which version to keep.
 *  - Logging out wipes this device's copy (after a final save) so the next
 *    person on a shared computer starts clean.
 */

export interface AccountUser {
  id: string;
  username: string;
  displayName: string;
  createdAt: number;
}

export type SyncStatus = 'idle' | 'saving' | 'saved' | 'offline' | 'error';

export interface SyncConflict {
  /** 'login': this device has progress that is not in the account. 'edit': both changed since the last sync. */
  reason: 'login' | 'edit';
  remote: AppState;
  remoteJson: string;
  remoteRevision: number;
  remoteUpdatedAt: number | null;
}

export interface AccountSnapshot {
  phase: 'checking' | 'guest' | 'signed-in' | 'unavailable';
  user: AccountUser | null;
  sync: SyncStatus;
  lastSyncedAt: number | null;
  error: string | null;
  conflict: SyncConflict | null;
  /** Shown once after signup / recovery / regeneration, then forgotten. */
  recoveryCode: string | null;
  /** The session ended (expired or logged out elsewhere) while this device still holds that account's progress. */
  sessionEnded: boolean;
}

interface Meta {
  userId: string | null;
  username: string | null;
  displayName: string | null;
  revision: number;
  dirty: boolean;
  lastSyncedAt: number | null;
}

const META_KEY = 'studio.account.v1';
const EMPTY_META: Meta = { userId: null, username: null, displayName: null, revision: 0, dirty: false, lastSyncedAt: null };
const DEBOUNCE_MS = 3000;
const MAX_WAIT_MS = 20_000;
const RETRY_MS = 30_000;
const MAX_UPLOAD_CHARS = 1_800_000;
const KEEPALIVE_LIMIT = 60_000;

const hasWindow = typeof window !== 'undefined';

function loadMeta(): Meta {
  try {
    const raw = localStorage.getItem(META_KEY);
    return raw ? { ...EMPTY_META, ...(JSON.parse(raw) as Partial<Meta>) } : { ...EMPTY_META };
  } catch {
    return { ...EMPTY_META };
  }
}

let meta: Meta = { ...EMPTY_META };
function saveMeta() {
  try {
    localStorage.setItem(META_KEY, JSON.stringify(meta));
  } catch {
    /* storage unavailable */
  }
}

let snap: AccountSnapshot = {
  phase: 'checking', user: null, sync: 'idle', lastSyncedAt: null, error: null, conflict: null, recoveryCode: null, sessionEnded: false,
};
const listeners = new Set<() => void>();
function set(patch: Partial<AccountSnapshot>) {
  snap = { ...snap, ...patch };
  listeners.forEach((l) => l());
}

export const getAccount = () => snap;
export function useAccount(): AccountSnapshot {
  return useSyncExternalStore((cb) => {
    listeners.add(cb);
    return () => listeners.delete(cb);
  }, getAccount, getAccount);
}

let store: Store | null = null;
/** The simulated demo profile is never uploaded or offered as "this device's progress". */
const isReal = (s: AppState) => !!s.profile && !s.profile.isDemo;
let applying = false;
let lastSaved: string | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let dirtySince = 0;

/** Serialises network work so a save never races a download or a conflict choice. */
let queue: Promise<unknown> = Promise.resolve();
function exclusive<T>(fn: () => Promise<T>): Promise<T> {
  const run = queue.then(fn, fn);
  queue = run.catch(() => undefined);
  return run;
}

/* ------------------------------------------------------------------ */
/* Start-up                                                             */
/* ------------------------------------------------------------------ */

export function startAccount(s: Store): Promise<void> {
  if (store === s) return queue.then(() => undefined);
  store = s;
  meta = loadMeta();
  s.subscribe(onLocalChange);
  if (hasWindow) {
    window.addEventListener('online', () => {
      if (snap.phase === 'signed-in' || meta.userId) void exclusive(refresh);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void flushOnHide();
      else if (snap.phase === 'signed-in' && !meta.dirty && !snap.conflict) void exclusive(() => checkRemote());
    });
  }
  return exclusive(refresh);
}

async function refresh() {
  try {
    const { user } = await api<{ user: AccountUser | null }>('GET', '/api/auth/me');
    if (!user) {
      set({ phase: 'guest', user: null, sync: 'idle', sessionEnded: !!meta.userId });
      return;
    }
    set({ phase: 'signed-in', user, sessionEnded: false });
    await reconcile(user);
  } catch (e) {
    const code = e instanceof ApiError ? e.code : 'error';
    if (code === 'offline' && meta.userId) {
      // Keep working offline as that account; changes are saved when the connection returns.
      set({ phase: 'signed-in', user: { id: meta.userId, username: meta.username ?? '', displayName: meta.displayName ?? '', createdAt: 0 }, sync: 'offline', lastSyncedAt: meta.lastSyncedAt });
    } else {
      set({ phase: code === 'offline' ? 'guest' : 'unavailable', user: null });
    }
  }
}

/* ------------------------------------------------------------------ */
/* Download / compare                                                   */
/* ------------------------------------------------------------------ */

interface Remote { revision: number; updatedAt: number | null; state: AppState | null; json: string | null }

async function fetchRemote(): Promise<Remote> {
  const r = await api<{ revision: number; format: string | null; data: string | null; updatedAt: number | null }>('GET', '/api/progress');
  if (!r.revision || !r.format || !r.data) return { revision: 0, updatedAt: null, state: null, json: null };
  const json = await decodeProgress(r.format, r.data);
  return { revision: r.revision, updatedAt: r.updatedAt, state: migrate(JSON.parse(json) as Partial<AppState>), json };
}

function applyRemote(user: AccountUser, remote: Remote) {
  applying = true;
  try {
    store!.replace(remote.state!);
  } finally {
    applying = false;
  }
  meta = { userId: user.id, username: user.username, displayName: user.displayName, revision: remote.revision, dirty: false, lastSyncedAt: remote.updatedAt };
  lastSaved = remote.json;
  saveMeta();
  set({ sync: 'saved', lastSyncedAt: remote.updatedAt, error: null, conflict: null });
}

function own(user: AccountUser, revision: number, dirty: boolean) {
  meta = { userId: user.id, username: user.username, displayName: user.displayName, revision, dirty, lastSyncedAt: meta.userId === user.id ? meta.lastSyncedAt : null };
  saveMeta();
}

/** Decide what to do after login / page load, comparing this device with the account. */
async function reconcile(user: AccountUser) {
  const hasLocal = isReal(store!.getState());
  const sameOwner = meta.userId === user.id;
  const remote = await fetchRemote();

  if (remote.revision === 0) {
    // Empty account: this device's progress (if any) becomes the account's.
    own(user, 0, hasLocal);
    if (hasLocal) await save(true);
    else set({ sync: 'idle', lastSyncedAt: null });
    return;
  }
  if (!hasLocal) return applyRemote(user, remote);
  if (sameOwner && remote.revision === meta.revision) {
    if (meta.dirty) await save(true);
    else set({ sync: 'saved', lastSyncedAt: meta.lastSyncedAt });
    return;
  }
  if (sameOwner && !meta.dirty) return applyRemote(user, remote);
  set({ conflict: { reason: sameOwner ? 'edit' : 'login', remote: remote.state!, remoteJson: remote.json!, remoteRevision: remote.revision, remoteUpdatedAt: remote.updatedAt } });
}

/** Lightweight check for progress saved on another device. */
async function checkRemote() {
  const user = snap.user;
  if (!user || snap.phase !== 'signed-in' || snap.conflict) return;
  try {
    const r = await api<{ revision: number; unchanged?: boolean }>('GET', `/api/progress?since=${meta.revision}`);
    if (r.unchanged || r.revision === meta.revision) return;
    await reconcile(user);
  } catch (e) {
    handleError(e);
  }
}

/* ------------------------------------------------------------------ */
/* Upload                                                               */
/* ------------------------------------------------------------------ */

function onLocalChange() {
  if (applying || !store || snap.phase !== 'signed-in' || meta.userId !== snap.user?.id) return;
  if (!meta.dirty) {
    meta.dirty = true;
    dirtySince = Date.now();
    saveMeta();
  }
  if (!snap.conflict) schedule();
}

function schedule(delay?: number) {
  if (timer) clearTimeout(timer);
  const wait = delay ?? Math.max(0, Math.min(DEBOUNCE_MS, dirtySince + MAX_WAIT_MS - Date.now()));
  timer = setTimeout(() => void exclusive(() => save(false)), wait);
}

async function payload(state: AppState) {
  let json = JSON.stringify(trimForStorage(state));
  let body = await encodeProgress(json);
  if (body.data.length > MAX_UPLOAD_CHARS) {
    // Very long histories: keep the most recent part (estimates and mastery are kept in full).
    json = JSON.stringify(trimForStorage(state, 0.25));
    body = await encodeProgress(json);
  }
  return { json, body };
}

async function save(force: boolean, opts: { keepalive?: boolean } = {}) {
  if (!store || snap.phase !== 'signed-in' || !snap.user || snap.conflict) return;
  if (!meta.dirty && !force) return;
  const state = store.getState();
  if (!isReal(state)) {
    meta.dirty = false;
    saveMeta();
    return;
  }
  set({ sync: 'saving' });
  try {
    const { json, body } = await payload(state);
    if (json === lastSaved) {
      meta.dirty = false;
      saveMeta();
      set({ sync: 'saved', error: null });
      return;
    }
    const keepalive = opts.keepalive && body.data.length < KEEPALIVE_LIMIT;
    const res = await api<{ revision: number; updatedAt: number }>('PUT', '/api/progress', { baseRevision: meta.revision, ...body }, { keepalive });
    lastSaved = json;
    meta = { ...meta, revision: res.revision, dirty: store.getState() !== state, lastSyncedAt: res.updatedAt };
    saveMeta();
    set({ sync: 'saved', lastSyncedAt: res.updatedAt, error: null });
    if (meta.dirty) schedule();
  } catch (e) {
    if (e instanceof ApiError && e.code === 'conflict') await reconcile(snap.user);
    else handleError(e);
  }
}

function handleError(e: unknown) {
  const code = e instanceof ApiError ? e.code : 'error';
  if (code === 'not_signed_in') {
    set({ phase: 'guest', user: null, sync: 'idle', sessionEnded: true });
    return;
  }
  set({ sync: code === 'offline' ? 'offline' : 'error', error: code });
  if (meta.dirty) schedule(RETRY_MS);
}

async function flushOnHide() {
  if (!meta.dirty || snap.phase !== 'signed-in') return;
  if (timer) clearTimeout(timer);
  await exclusive(() => save(false, { keepalive: true }));
}

/* ------------------------------------------------------------------ */
/* Public actions                                                       */
/* ------------------------------------------------------------------ */

async function afterAuth(user: AccountUser, recoveryCode: string | null) {
  set({ phase: 'signed-in', user, sessionEnded: false, error: null, recoveryCode });
  await reconcile(user);
}

/** Rejects weak passwords before anything is sent (the server never sees the password). */
function checkStrength(password: string, username: string) {
  const problem = passwordProblem(password, username);
  if (problem) throw new ApiError(400, problem);
}

export function signUp(username: string, password: string) {
  return exclusive(async () => {
    checkStrength(password, username);
    const r = await api<{ user: AccountUser; recoveryCode: string }>('POST', '/api/auth/signup', { username, passwordKey: await passwordKey(username, password) });
    await afterAuth(r.user, r.recoveryCode);
  });
}

export function logIn(username: string, password: string) {
  return exclusive(async () => {
    const r = await api<{ user: AccountUser }>('POST', '/api/auth/login', { username, passwordKey: await passwordKey(username, password) });
    await afterAuth(r.user, null);
  });
}

export function recoverAccount(username: string, recoveryCode: string, newPassword: string) {
  return exclusive(async () => {
    checkStrength(newPassword, username);
    const r = await api<{ user: AccountUser; recoveryCode: string }>('POST', '/api/auth/recover', { username, recoveryCode, newPasswordKey: await passwordKey(username, newPassword) });
    await afterAuth(r.user, r.recoveryCode);
  });
}

/** Keep the account's version or this device's version after a conflict. */
export function resolveConflict(keep: 'account' | 'device') {
  return exclusive(async () => {
    const c = snap.conflict;
    const user = snap.user;
    if (!c || !user) return;
    if (keep === 'account') {
      applyRemote(user, { revision: c.remoteRevision, updatedAt: c.remoteUpdatedAt, state: c.remote, json: c.remoteJson });
      return;
    }
    own(user, c.remoteRevision, true);
    set({ conflict: null });
    await save(true);
  });
}

export function syncNow() {
  return exclusive(async () => {
    if (!snap.user || snap.phase !== 'signed-in') return refresh();
    try {
      if (meta.dirty) await save(true);
      else await reconcile(snap.user);
    } catch (e) {
      handleError(e);
    }
  });
}

/** Clears this device: progress, sync state, chat history and (optionally) the AI key. */
function wipeDevice(removeAiKey: boolean) {
  meta = { ...EMPTY_META };
  saveMeta();
  lastSaved = null;
  if (timer) clearTimeout(timer);
  clearChat();
  if (removeAiKey) setApiKey('');
  applying = true;
  try {
    store?.reset();
  } finally {
    applying = false;
  }
  set({ phase: 'guest', user: null, sync: 'idle', lastSyncedAt: null, error: null, conflict: null, recoveryCode: null, sessionEnded: false });
}

/**
 * Saves any last changes, ends the session and wipes this device.
 * Returns `unsynced: true` (and does nothing) if changes could not be saved
 * and `force` is not set, so the UI can ask before discarding them.
 */
export function logOut(opts: { everywhere?: boolean; force?: boolean; removeAiKey?: boolean } = {}) {
  return exclusive(async (): Promise<{ ok: boolean; unsynced?: boolean }> => {
    if (meta.dirty && snap.phase === 'signed-in' && !snap.conflict) await save(true);
    if ((meta.dirty || snap.conflict) && !opts.force) return { ok: false, unsynced: true };
    try {
      await api('POST', '/api/auth/logout', { everywhere: !!opts.everywhere });
    } catch {
      /* the device is wiped either way */
    }
    wipeDevice(!!opts.removeAiKey);
    return { ok: true };
  });
}

/** Key for the signed-in user's password. */
function myKey(password: string) {
  const username = snap.user?.username;
  if (!username) throw new ApiError(401, 'not_signed_in');
  return passwordKey(username, password);
}

export async function changePassword(currentPassword: string, newPassword: string) {
  checkStrength(newPassword, snap.user?.username ?? '');
  const [currentPasswordKey, newPasswordKey] = await Promise.all([myKey(currentPassword), myKey(newPassword)]);
  return api<{ ok: boolean }>('POST', '/api/auth/password', { currentPasswordKey, newPasswordKey });
}

export async function newRecoveryCode(password: string) {
  const r = await api<{ recoveryCode: string }>('POST', '/api/auth/recovery-code', { passwordKey: await myKey(password) });
  set({ recoveryCode: r.recoveryCode });
}

export const dismissRecoveryCode = () => set({ recoveryCode: null });

/** Deletes the account on the server. The device copy is wiped unless `keepAsGuest`. */
export function deleteAccount(password: string, keepAsGuest: boolean) {
  return exclusive(async () => {
    await api('POST', '/api/account/delete', { passwordKey: await myKey(password) });
    if (keepAsGuest) {
      meta = { ...EMPTY_META };
      saveMeta();
      set({ phase: 'guest', user: null, sync: 'idle', lastSyncedAt: null, conflict: null, recoveryCode: null });
    } else {
      wipeDevice(false);
    }
  });
}

/** Test hook: forget module state between test cases. */
export function _resetAccountForTests() {
  store = null;
  meta = { ...EMPTY_META };
  lastSaved = null;
  if (timer) clearTimeout(timer);
  queue = Promise.resolve();
  snap = { phase: 'checking', user: null, sync: 'idle', lastSyncedAt: null, error: null, conflict: null, recoveryCode: null, sessionEnded: false };
}

/** Test hook: run the pending debounced save immediately. */
export function _flushForTests() {
  if (timer) clearTimeout(timer);
  return exclusive(() => save(false));
}
